<template>
  <div class="wm-shop-container">
    <div v-if="!isExpanded" class="compact-promo-card" @click="isExpanded = true">
      <div class="promo-text-area">
        <div class="promo-badge">WELL COME FIT EXCLUSIVE</div>
        <h3 class="promo-title">SCHWEIZ FAN-TRIKOT WM26</h3>
        <p class="promo-subtitle">Nur CHF 15.90</p>
      </div>
      <div class="promo-image-area">
        <img
          src="/img/einmalig/fantrikots/wcf_merchandise_mockup_wm-trikot_mann_front.jpg"
          style="height: 74px; width: 75px; object-fit: contain"
          alt="Trikot" />
        <div class="arrow-circle">
          <v-icon color="white" small>mdi-arrow-right</v-icon>
        </div>
      </div>
    </div>

    <transition name="shop-fade">
      <div v-if="isExpanded" class="shop-overlay">
        <div class="shop-container">
          <header class="shop-header">
            <v-btn icon dark @click="closeModal" class="close-btn">
              <v-icon size="32">mdi-close</v-icon>
            </v-btn>
            <div class="header-titles">
              <span class="swiss-red--text font-weight-bold">SWITZERLAND SELECTION</span>
              <h1>HOPP SCHWIIZ</h1>
            </div>
          </header>

          <div class="shop-content-scroll">
            <div v-if="currentView === 'shop'">
              <div v-if="existingOrders.length > 0" class="pickup-invitation mb-6">
                <h3 class="pickup-title">
                  <v-icon color="white" left>mdi-ticket-confirmation</v-icon>
                  Deine Abholungseinladung
                </h3>
                <p class="pickup-text">
                  Die Lieferzeit für die Trikots beträgt aktuell <strong>2–3 Wochen</strong>. Wir informieren dich selbstverständlich per E-Mail,
                  sobald deine Bestellung an der Theke bereitliegt.
                </p>

                <div v-for="order in existingOrders" :key="order._id" class="reservation-card mb-3">
                  <div v-for="(item, j) in order.content.items" :key="j" class="d-flex justify-space-between text-caption mb-1">
                    <span class="font-weight-bold">{{ item.quantity }}x {{ item.variantName }}</span>
                    <span>Gr. {{ item.size }}</span>
                  </div>
                  <div class="d-flex justify-space-between align-center mt-3 pt-2" style="border-top: 1px solid rgba(255, 255, 255, 0.1)">
                    <span class="status-badge" :class="order.content.status === 'reserved' ? 'bg-warning' : 'bg-success'">
                      {{ order.content.status === "reserved" ? "RESERVIERT" : "ABGEHOLT" }}
                    </span>
                    <v-btn v-if="order.content.status === 'reserved'" text color="error" x-small @click="cancelOrder(order._id)"> Stornieren </v-btn>
                  </div>
                </div>
              </div>

              <div class="category-nav">
                <button @click="setCategory('mann')" :class="{ active: cat === 'mann' }">Herren</button>
                <button @click="setCategory('frau')" :class="{ active: cat === 'frau' }">Damen</button>
                <button @click="setCategory('kinder')" :class="{ active: cat === 'kinder' }">Kids</button>
              </div>

              <div class="product-stage">
                <div class="image-viewer">
                  <v-carousel hide-delimiters height="400" class="main-carousel" show-arrows-on-hover>
                    <v-carousel-item v-for="(img, i) in currentImages" :key="i" :src="`/img/einmalig/fantrikots/${img}`" contain></v-carousel-item>
                  </v-carousel>
                </div>

                <div class="configurator">
                  <div class="product-info">
                    <h2 class="variant-title mt-3">{{ displayTitle }}</h2>
                  </div>

                  <div class="marketing-description">
                    <p class="lead-text">Zeige deine Unterstützung für die Schweiz mit dem exklusiven WELL COME FIT Fan-Trikot zur WM 2026.</p>
                    <p class="body-text">
                      Ob beim Public Viewing, im Stadion, beim Sport oder im Alltag – mit diesem Trikot trägst du den Schweizer Spirit überall mit
                      dir. Das moderne Design kombiniert sportlichen Style mit maximalem Komfort und macht das Trikot zum perfekten Begleiter für
                      echte Fans. Der hochwertige Stoff sorgt für ein angenehmes Tragegefühl und einen authentischen Team-Look.
                    </p>
                    <div class="slogan-badge">🔥 Bereit für die WM 2026 – HOPP SCHWIIZ!</div>
                  </div>

                  <div class="size-picker">
                    <div class="d-flex justify-space-between align-center mb-3">
                      <span class="label">Grösse wählen</span>
                      <span class="selected-size-display" v-if="selectedSize">{{ selectedSize }}</span>
                    </div>

                    <div class="size-grid" :class="{ 'kids-grid': cat === 'kinder' }">
                      <div
                        v-for="size in availableSizes"
                        :key="size"
                        @click="selectedSize = size"
                        :class="['size-item', { active: selectedSize === size }]">
                        {{ size }}
                      </div>
                    </div>
                  </div>

                  <div class="quantity-row">
                    <span class="label">Anzahl</span>
                    <div class="q-stepper">
                      <button @click="q > 1 ? q-- : null" class="q-btn">−</button>
                      <span class="q-val">{{ q }}</span>
                      <button @click="q < 10 ? q++ : null" class="q-btn">+</button>
                    </div>
                  </div>

                  <v-btn block x-large class="add-to-cart-btn" @click="addToCart"> ZUM WARENKORB — CHF {{ (15.9 * q).toFixed(2) }} </v-btn>
                </div>
              </div>

              <div v-if="cart.length > 0" style="height: 80px"></div>
            </div>

            <div v-else-if="currentView === 'cart'" class="checkout-view">
              <div class="checkout-header d-flex align-center mb-6">
                <v-btn icon dark @click="currentView = 'shop'" class="mr-2">
                  <v-icon>mdi-arrow-left</v-icon>
                </v-btn>
                <h2 class="m-0">Dein Warenkorb</h2>
              </div>

              <div class="cart-items-wrapper">
                <div v-for="(item, i) in cart" :key="i" class="cart-item">
                  <div class="cart-item-details">
                    <span class="font-weight-bold d-block">{{ item.q }}x {{ item.name }}</span>
                    <span class="text-caption grey--text">Grösse: {{ item.size }}</span>
                  </div>
                  <div class="cart-item-price d-flex align-center">
                    CHF {{ (15.9 * item.q).toFixed(2) }}
                    <v-btn icon small color="error" class="ml-2" @click="removeFromCart(i)">
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </div>
              </div>

              <div class="total-price-display mt-4 mb-6">
                <span>Total:</span>
                <span class="font-weight-bold red--text text-h5">CHF {{ cartTotal }}</span>
              </div>

              <div class="info-box-checkout mb-6">
                <div class="d-flex">
                  <v-icon class="info-icon mt-1 mr-3" color="#d52b1e">mdi-package-variant-closed</v-icon>
                  <div>
                    <strong class="d-block mb-1 text-white">Wichtige Information zur Abholung:</strong>
                    <p class="info-text-modal">
                      Die Trikots sind spätestens ab der <strong>Woche vom 1. Juni</strong> an der Theke abholbereit (Zahlung vor Ort). Sobald deine
                      Bestellung bereit ist, wirst du selbstverständlich per E-Mail informiert.
                    </p>
                  </div>
                </div>
              </div>

              <v-btn block color="#d52b1e" dark x-large class="submit-order-btn" @click="submitPreOrder" :loading="loading">
                VORBESTELLEN (CHF {{ cartTotal }})
              </v-btn>
            </div>
          </div>

          <transition name="bounce-up">
            <div v-if="currentView === 'shop' && cart.length > 0" class="floating-cart-wrapper">
              <button class="cart-pill" @click="currentView = 'cart'">
                <div class="cart-badge">{{ cartItemCount }}</div>
                <span>Warenkorb öffnen</span>
                <v-icon color="white" small class="ml-2">mdi-arrow-right</v-icon>
              </button>
            </div>
          </transition>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import api from "../api"

export default {
  data() {
    return {
      isExpanded: false,
      loading: false,
      currentView: "shop",
      cat: "mann",
      selectedSize: "",
      q: 1,
      cart: [],
      existingOrders: [],
      images: {
        mann: [
          "wcf_merchandise_mockup_wm-trikot_mann_front.jpg",
          "wcf_merchandise_mockup_wm-trikot_mann_back.jpg",
          "wcf_merchandise_mockup_wm-trikot_mann_closeup.jpg",
        ],
        frau: [
          "wcf_merchandise_mockup_wm-trikot_frau_front.jpg",
          "wcf_merchandise_mockup_wm-trikot_frau_back.jpg",
          "wcf_merchandise_mockup_wm-trikot_frau_closeup.jpg",
        ],
        kinder_m: [
          "wcf_merchandise_mockup_wm-trikot_junge_front.jpg",
          "wcf_merchandise_mockup_wm-trikot_junge_back.jpg",
          "wcf_merchandise_mockup_wm-trikot_junge_closeup.jpg",
        ],
        kinder_f: [
          "wcf_merchandise_mockup_wm-trikot_mädchen_front.jpg",
          "wcf_merchandise_mockup_wm-trikot_mädchen_back.jpg",
          "wcf_merchandise_mockup_wm-trikot_mädchen_closeup.jpg",
        ],
      },
    }
  },
  computed: {
    displayTitle() {
      if (this.cat === "mann") return "HERREN EDITION"
      if (this.cat === "frau") return "DAMEN EDITION"
      return "KINDER & JUGEND"
    },
    currentImages() {
      if (this.cat === "mann") return this.images.mann
      if (this.cat === "frau") return this.images.frau
      return this.images.kinder_m
    },
    availableSizes() {
      if (this.cat === "mann") return ["S", "M", "L", "XL", "XXL"]
      if (this.cat === "frau") return ["XS", "S", "M", "L", "XL", "XXL"]
      return ["5J", "6J", "7J", "8J", "9J", "10J", "11J", "12J", "13J", "14J", "15J"]
    },
    cartTotal() {
      return this.cart.reduce((sum, i) => sum + 15.9 * i.q, 0).toFixed(2)
    },
    cartItemCount() {
      return this.cart.reduce((count, item) => count + item.q, 0)
    },
  },
  async created() {
    this.fetchOrders()
  },
  methods: {
    closeModal() {
      this.isExpanded = false
      this.currentView = "shop"
      this.cart = []
    },
    setCategory(c) {
      this.cat = c
      this.selectedSize = ""
      this.q = 1
    },
    addToCart() {
      if (!this.selectedSize) {
        this.$toast.error("Wähle zuerst eine Grösse!")
        return
      }
      this.cart.push({
        name: this.displayTitle,
        type: this.cat,
        size: this.selectedSize,
        q: this.q,
        variantName: this.cat === "kinder" ? `Kids (${this.selectedSize})` : this.cat.toUpperCase(),
      })
      this.q = 1
      this.$toast.success("Bestellung hinzugefügt")
    },
    removeFromCart(index) {
      this.cart.splice(index, 1)
      if (this.cart.length === 0) {
        this.currentView = "shop"
      }
    },
    async submitPreOrder() {
      this.loading = true
      try {
        const formattedItems = this.cart.map((i) => ({
          type: i.type,
          size: i.size,
          quantity: i.q,
          variantName: i.variantName,
        }))
        await api.reserveTrikot(formattedItems, this.cartTotal)
        this.$toast.success("Vorbestellung erfolgreich!")
        this.cart = []
        this.currentView = "shop"
        this.fetchOrders()
      } catch (e) {
        this.$toast.error("Fehler beim Senden.")
      } finally {
        this.loading = false
      }
    },
    async fetchOrders() {
      const data = await api.getTrikotActionStatus()
      this.existingOrders = data.reservations || []
    },
    async cancelOrder(id) {
      if (confirm("Möchtest du diese Vorbestellung wirklich stornieren?")) {
        await api.cancelTrikotReservation(id)
        this.$toast.success("Vorbestellung storniert.")
        this.fetchOrders()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.wm-shop-container {
  font-family: "Inter", sans-serif;
  color: #fff;
  padding: 0 16px;
  margin-bottom: 16px;
}

/* ==========================================
   HOMESCREEN BANNER (Kompakt)
   ========================================== */
.compact-promo-card {
  background: linear-gradient(135deg, #1e1e1e 0%, #2a2a2a 100%);
  border-radius: 16px;
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s;

  &:active {
    transform: scale(0.98);
  }

  .promo-text-area {
    flex: 1;
    padding-right: 12px;
  }

  .promo-badge {
    color: #d52b1e;
    font-size: 0.65rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 6px;
  }

  .promo-title {
    font-size: 1.15rem;
    font-weight: 900;
    color: white;
    line-height: 1.2;
    margin: 0 0 6px 0;
  }

  .promo-subtitle {
    font-size: 0.9rem;
    color: #bbb;
    margin: 0;
    display: flex;
    align-items: center;

    .tag {
      font-size: 0.6rem;
      background: rgba(255, 255, 255, 0.15);
      color: #fff;
      padding: 2px 6px;
      border-radius: 8px;
      margin-left: 8px;
    }
  }

  .promo-image-area {
    position: relative;
    width: 75px;
    height: 75px;
    border-radius: 12px;
    background: #cdcdcd;
    border: 1px solid rgba(255, 255, 255, 0.05);

    .arrow-circle {
      position: absolute;
      bottom: -8px;
      right: -8px;
      background: #d52b1e;
      border-radius: 50%;
      padding: 6px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
}

/* ==========================================
   MODAL / SHOP OVERLAY
   ========================================== */
.shop-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #000;
  z-index: 9999;
  display: flex;
  flex-direction: column;
}

.shop-container {
  height: 100%;
  max-width: 600px;
  margin: 0 auto;
  width: 100%;
  background: #111;
  display: flex;
  flex-direction: column;
  position: relative;
}

.shop-header {
  padding: 40px 20px 20px;
  position: relative;
  .close-btn {
    position: absolute;
    top: 10px;
    right: 10px;
  }
  .header-titles {
    h1 {
      font-size: 3rem;
      font-weight: 900;
      letter-spacing: -2px;
      line-height: 1;
    }
  }
}

.shop-content-scroll {
  flex: 1;
  overflow-y: auto;
  padding: 0 20px 40px;
}

/* Marketing Description Block */
.marketing-description {
  background: rgba(255, 255, 255, 0.03);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 28px;
  border-left: 3px solid #d52b1e;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);

  .lead-text {
    font-weight: 700;
    font-size: 0.95rem;
    color: #fff;
    margin-bottom: 8px;
    line-height: 1.4;
  }

  .body-text {
    font-size: 0.85rem;
    color: #bbb;
    line-height: 1.5;
    margin-bottom: 14px;
  }

  .slogan-badge {
    display: inline-block;
    background: rgba(213, 43, 30, 0.15);
    color: #ff6b6b;
    font-weight: 800;
    font-size: 0.8rem;
    padding: 6px 12px;
    border-radius: 8px;
  }
}

/* Category Nav */
.category-nav {
  display: flex;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 30px;
  padding: 4px;
  margin-bottom: 30px;
  button {
    flex: 1;
    padding: 10px;
    border-radius: 25px;
    font-size: 0.9rem;
    font-weight: bold;
    color: #888;
    &.active {
      background: #fff;
      color: #000;
    }
  }
}

/* Configurator */
.variant-title {
  font-size: 1.5rem;
  font-weight: 900;
}
.material-info {
  font-size: 0.8rem;
  color: #666;
  margin-bottom: 20px;
}
.label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #555;
  font-weight: bold;
}

.size-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 10px;
  margin-bottom: 30px;

  &.kids-grid {
    grid-template-columns: repeat(4, 1fr);
  }

  .size-item {
    background: #1a1a1a;
    border: 1px solid #222;
    padding: 15px 5px;
    text-align: center;
    border-radius: 8px;
    font-weight: bold;
    font-size: 0.9rem;
    cursor: pointer;
    &.active {
      border-color: #d52b1e;
      color: #d52b1e;
      background: rgba(213, 43, 30, 0.1);
    }
  }
}

.quantity-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
}

.q-stepper {
  display: flex;
  align-items: center;
  gap: 20px;
  background: #1a1a1a;
  padding: 5px 20px;
  border-radius: 30px;
  .q-btn {
    font-size: 1.5rem;
    color: #d52b1e;
  }
  .q-val {
    font-size: 1.2rem;
    font-weight: bold;
    min-width: 20px;
    text-align: center;
  }
}

.add-to-cart-btn {
  background: #d52b1e !important;
  color: white !important;
  font-weight: 900 !important;
  letter-spacing: 1px;
  border-radius: 12px;
}

/* Schwebender Warenkorb (Floating Pill) */
.floating-cart-wrapper {
  position: absolute;
  bottom: 30px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  z-index: 1010;
  pointer-events: none;
}

.cart-pill {
  pointer-events: auto;
  background: #d52b1e;
  color: white;
  padding: 12px 24px 12px 16px;
  border-radius: 30px;
  font-weight: bold;
  font-size: 1.1rem;
  box-shadow: 0 4px 15px rgba(213, 43, 30, 0.5);
  display: flex;
  align-items: center;
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: transform 0.2s;

  &:active {
    transform: scale(0.95);
  }
}

.cart-badge {
  background: white;
  color: #d52b1e;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  font-weight: 900;
  margin-right: 12px;
}

/* Checkout View */
.checkout-view {
  animation: fade-in 0.3s ease-out;
}

.cart-items-wrapper {
  margin-bottom: 20px;
}

.cart-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(255, 255, 255, 0.05);
  padding: 16px;
  border-radius: 12px;
  margin-bottom: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.total-price-display {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 16px;
}

.info-box-checkout {
  background: rgba(213, 43, 30, 0.1);
  border: 1px solid rgba(213, 43, 30, 0.3);
  border-radius: 12px;
  padding: 16px;
}

.info-text-modal {
  font-size: 0.85rem;
  color: #bdc3c7;
  margin: 0;
  line-height: 1.4;
}

.submit-order-btn {
  font-weight: 900 !important;
  letter-spacing: 1px;
  border-radius: 12px;
}

/* Abholungseinladung (Bestehende Bestellungen) */
.pickup-invitation {
  background: linear-gradient(135deg, #1e293b, #0f172a);
  border: 1px solid #334155;
  border-radius: 16px;
  padding: 20px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.5);
}

.pickup-invitation::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  background: #d52b1e;
}

.pickup-title {
  color: white;
  font-size: 1.15rem;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
}

.pickup-text {
  color: #bdc3c7;
  font-size: 0.85rem;
  line-height: 1.5;
  margin-bottom: 16px;
}

.reservation-card {
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 12px;
}

.status-badge {
  font-size: 0.6rem;
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: bold;
  &.bg-warning {
    background: #f59e0b;
    color: #fff;
  }
  &.bg-success {
    background: #10b981;
    color: #fff;
  }
}

/* Animations */
.shop-fade-enter-active,
.shop-fade-leave-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}
.shop-fade-enter,
.shop-fade-leave-to {
  opacity: 0;
  transform: translateY(100px);
}

@keyframes fade-in {
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.bounce-up-enter-active {
  animation: bounce-in-up 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}
.bounce-up-leave-active {
  animation: bounce-in-up 0.3s reverse;
}
@keyframes bounce-in-up {
  0% {
    opacity: 0;
    transform: translateY(60px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
